import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  createSupportCase,
  getMySupportCases,
  SupportCasePriority,
  SupportCaseStatus,
  SupportCaseSummary,
} from "../api/support";
import { buildApiUrl } from "../helpers/base_url";

const SupportCenter: React.FC = () => {
  const [currentUser, setCurrentUser] = useState<any>(() => {
    if (typeof window === "undefined") return null;
    try {
      return JSON.parse(localStorage.getItem("user") || "null");
    } catch {
      return null;
    }
  });
  const [hasToken, setHasToken] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    try {
      return !!localStorage.getItem("accessToken");
    } catch {
      return false;
    }
  });

  const normalizedUserType = useMemo(
    () => (currentUser?.userType || "").toUpperCase(),
    [currentUser]
  );
  const canUseSupport = hasToken && normalizedUserType === "CLIENT";

  const [supportCaseForm, setSupportCaseForm] = useState({
    subject: "",
    priority: "LOW" as SupportCasePriority,
    description: "",
    attachmentUrl: "",
  });
  const [supportSubmitting, setSupportSubmitting] = useState(false);
  const [supportStatus, setSupportStatus] = useState<{
    type: "idle" | "success" | "error";
    message: string;
    caseNumber?: string;
  }>({
    type: "idle",
    message: "",
  });
  const [supportAttachmentUploading, setSupportAttachmentUploading] =
    useState(false);
  const [supportAttachmentName, setSupportAttachmentName] = useState("");
  const [supportAttachmentError, setSupportAttachmentError] = useState("");

  const [supportCases, setSupportCases] = useState<SupportCaseSummary[]>([]);
  const [supportCasesLoading, setSupportCasesLoading] = useState(false);
  const [supportCasesError, setSupportCasesError] = useState("");
  const [supportCaseFilter, setSupportCaseFilter] = useState<
    SupportCaseStatus | ""
  >("");

  useEffect(() => {
    const syncAuthState = () => {
      if (typeof window === "undefined") return;
      try {
        const storedUser = localStorage.getItem("user");
        setCurrentUser(storedUser ? JSON.parse(storedUser) : null);
        setHasToken(!!localStorage.getItem("accessToken"));
      } catch {
        setCurrentUser(null);
        setHasToken(false);
      }
    };

    syncAuthState();
    window.addEventListener("storage", syncAuthState);
    window.addEventListener("localStorageChange", syncAuthState);

    return () => {
      window.removeEventListener("storage", syncAuthState);
      window.removeEventListener("localStorageChange", syncAuthState);
    };
  }, []);

  const priorityOptions: SupportCasePriority[] = [
    "LOW",
    "MEDIUM",
    "HIGH",
    "URGENT",
  ];
  const statusFilterOptions: (SupportCaseStatus | "")[] = [
    "",
    "OPEN",
    "IN_PROGRESS",
    "WAITING_ON_CLIENT",
    "WAITING_ON_VENDOR",
    "RESOLVED",
    "CLOSED",
  ];

  const loadSupportCases = useCallback(async () => {
    if (!canUseSupport) {
      setSupportCases([]);
      setSupportCasesError("");
      setSupportCasesLoading(false);
      return;
    }
    setSupportCasesLoading(true);
    setSupportCasesError("");
    try {
      const cases = await getMySupportCases(supportCaseFilter);
      setSupportCases(cases);
    } catch (error: any) {
      setSupportCasesError(
        error?.message || "Failed to load support cases. Please try again."
      );
      setSupportCases([]);
    } finally {
      setSupportCasesLoading(false);
    }
  }, [canUseSupport, supportCaseFilter]);

  useEffect(() => {
    loadSupportCases();
  }, [loadSupportCases]);

  const handleSupportInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;
    setSupportCaseForm((prev) => ({
      ...prev,
      [name]: name === "priority" ? (value as SupportCasePriority) : value,
    }));
  };

  const handleSupportAttachmentUpload = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      setSupportAttachmentError("Attachment must be 5MB or less.");
      e.target.value = "";
      return;
    }

    setSupportAttachmentError("");
    setSupportAttachmentUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await fetch(buildApiUrl("/file/"), {
        method: "POST",
        body: formData,
      });

      const data = await response.json();
      if (response.ok && data?.success && data?.data) {
        setSupportCaseForm((prev) => ({
          ...prev,
          attachmentUrl: data.data,
        }));
        setSupportAttachmentName(file.name);
      } else {
        throw new Error(data?.message || "Failed to upload attachment");
      }
    } catch (error: any) {
      setSupportAttachmentError(
        error?.message || "Failed to upload attachment. Please try again."
      );
    } finally {
      setSupportAttachmentUploading(false);
      e.target.value = "";
    }
  };

  const removeSupportAttachment = () => {
    setSupportCaseForm((prev) => ({
      ...prev,
      attachmentUrl: "",
    }));
    setSupportAttachmentName("");
    setSupportAttachmentError("");
  };

  const handleSupportCaseSubmit = async (
    e: React.FormEvent<HTMLFormElement>
  ) => {
    e.preventDefault();
    if (!canUseSupport) {
      setSupportStatus({
        type: "error",
        message:
          "Please log in with your client account to open a support case.",
      });
      return;
    }

    if (
      !supportCaseForm.subject.trim() ||
      !supportCaseForm.description.trim()
    ) {
      setSupportStatus({
        type: "error",
        message: "Subject and description are required.",
      });
      return;
    }

    setSupportSubmitting(true);
    setSupportStatus({ type: "idle", message: "" });

    try {
      const payload = {
        subject: supportCaseForm.subject.trim(),
        description: supportCaseForm.description.trim(),
        priority: supportCaseForm.priority,
        ...(supportCaseForm.attachmentUrl
          ? { attachmentUrl: supportCaseForm.attachmentUrl }
          : {}),
      };

      const response = await createSupportCase(payload);
      const caseNumber = response?.data?.caseNumber;

      setSupportStatus({
        type: "success",
        message:
          response?.message ||
          "Support case created successfully. Our team will reach out shortly.",
        caseNumber,
      });

      setSupportCaseForm({
        subject: "",
        priority: "LOW",
        description: "",
        attachmentUrl: "",
      });
      setSupportAttachmentName("");
      setSupportAttachmentError("");
      loadSupportCases();
    } catch (error: any) {
      setSupportStatus({
        type: "error",
        message: error?.message || "Failed to create support case.",
      });
    } finally {
      setSupportSubmitting(false);
    }
  };

  const supportFieldsDisabled = !canUseSupport || supportSubmitting;
  const supportSubmitDisabled =
    !canUseSupport ||
    supportSubmitting ||
    !supportCaseForm.subject.trim() ||
    !supportCaseForm.description.trim();

  const handleSupportCaseFilterChange = (
    e: React.ChangeEvent<HTMLSelectElement>
  ) => {
    setSupportCaseFilter(e.target.value as SupportCaseStatus | "");
  };

  const formatCaseDate = (value?: string) => {
    if (!value) return "N/A";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleString(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    });
  };

  const formatStatusLabel = (status: string) =>
    status
      .toLowerCase()
      .split("_")
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(" ");

  const getPriorityBadgeColors = (priority: SupportCasePriority) => {
    switch (priority) {
      case "LOW":
        return { background: "#ecfdf5", color: "#065f46" };
      case "MEDIUM":
        return { background: "#fef3c7", color: "#92400e" };
      case "HIGH":
        return { background: "#fee2e2", color: "#b91c1c" };
      case "URGENT":
        return { background: "#ffe4e6", color: "#be123c" };
      default:
        return { background: "#e2e8f0", color: "#1e293b" };
    }
  };

  const getStatusBadgeColors = (status: SupportCaseStatus) => {
    switch (status) {
      case "OPEN":
        return { background: "#dbeafe", color: "#1d4ed8" };
      case "IN_PROGRESS":
        return { background: "#ede9fe", color: "#5b21b6" };
      case "WAITING_ON_CLIENT":
        return { background: "#fff7ed", color: "#c2410c" };
      case "WAITING_ON_VENDOR":
        return { background: "#fdf4ff", color: "#a21caf" };
      case "RESOLVED":
        return { background: "#ecfccb", color: "#3f6212" };
      case "CLOSED":
        return { background: "#f1f5f9", color: "#475569" };
      default:
        return { background: "#e2e8f0", color: "#1e293b" };
    }
  };

  return (
    <div style={{ background: "#f8fafc", minHeight: "100vh" }}>
      <section
        style={{
          padding: "140px 0 100px",
          background:
            "linear-gradient(135deg, #ede9fe 0%, rgba(255,255,255,0.95) 60%, #ffffff 100%)",
          color: "#0f172a",
        }}
      >
        <div
          style={{
            maxWidth: 1200,
            margin: "0 auto",
            padding: "0 20px",
            textAlign: "center",
          }}
        >
          <p
            style={{
              letterSpacing: 6,
              textTransform: "uppercase",
              fontWeight: 700,
              fontSize: 14,
              color: "#7c3aed",
            }}
          >
            Client Success Hub
          </p>
          <h1
            style={{
              fontSize: 54,
              fontWeight: 900,
              margin: "16px 0",
              color: "#1e1b4b",
            }}
          >
            Support Center
          </h1>
          <p
            style={{
              fontSize: 18,
              color: "rgba(15,23,42,0.75)",
              maxWidth: 760,
              margin: "0 auto",
              lineHeight: 1.8,
            }}
          >
            Create new support cases, track resolutions, and stay in the loop
            with every interaction. Our concierge team is ready to help.
          </p>
        </div>
      </section>

      <section
        id="create-case"
        style={{
          background: "#ffffff",
          padding: "80px 0",
        }}
      >
        <div
          style={{
            maxWidth: 1200,
            margin: "0 auto",
            padding: "0 20px",
          }}
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
              gap: 32,
              alignItems: "stretch",
            }}
          >
            <div
              style={{
                padding: 32,
                borderRadius: 24,
                background:
                  "linear-gradient(135deg, rgba(236,233,254,0.8), rgba(255,255,255,0.9))",
                border: "1px solid rgba(99,102,241,0.2)",
                color: "#312e81",
              }}
            >
              <p
                style={{
                  textTransform: "uppercase",
                  color: "#6d28d9",
                  letterSpacing: 4,
                  fontWeight: 700,
                  fontSize: 13,
                  marginBottom: 12,
                }}
              >
                Step-by-step
              </p>
              <h2
                style={{
                  fontSize: 36,
                  fontWeight: 800,
                  marginBottom: 20,
                  color: "#1e1b4b",
                }}
              >
                Share the context, we’ll handle the rest.
              </h2>
              <ul
                style={{
                  listStyle: "none",
                  padding: 0,
                  margin: 0,
                  display: "flex",
                  flexDirection: "column",
                  gap: 20,
                  fontSize: 16,
                  lineHeight: 1.7,
                  color: "#4338ca",
                }}
              >
                {[
                  "Describe your issue in detail and include event/vendor references.",
                  "Select the right priority so the case gets auto-routed properly.",
                  "Attach supporting files (screenshots, invoices, receipts).",
                  "Our agents will email you and update the case timeline.",
                ].map((item) => (
                  <li key={item} style={{ display: "flex", gap: 12 }}>
                    <span
                      style={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        background: "#7c3aed",
                        marginTop: 10,
                        flexShrink: 0,
                      }}
                    />
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            <div
              style={{
                background: "white",
                borderRadius: 24,
                padding: 36,
                boxShadow: "0 25px 50px rgba(15,23,42,0.4)",
                border: "1px solid rgba(15,23,42,0.15)",
              }}
            >
              <h3
                style={{
                  fontSize: 28,
                  fontWeight: 800,
                  color: "#0f172a",
                  marginBottom: 16,
                }}
              >
                Create a Support Case
              </h3>

              {!canUseSupport && (
                <div
                  style={{
                    background: "#fef3c7",
                    color: "#92400e",
                    padding: "12px 16px",
                    borderRadius: 10,
                    border: "1px solid #fde68a",
                    marginBottom: 20,
                    fontWeight: 500,
                  }}
                >
                  Please{" "}
                  <a href="/login" style={{ color: "#7c3aed", fontWeight: 700 }}>
                    log in
                  </a>{" "}
                  with your client account to submit support requests.
                </div>
              )}

              {supportStatus.type === "success" && (
                <div
                  style={{
                    background: "#dcfce7",
                    color: "#166534",
                    border: "1px solid #bbf7d0",
                    padding: "12px 16px",
                    borderRadius: 10,
                    marginBottom: 20,
                    fontWeight: 500,
                  }}
                >
                  {supportStatus.message}
                  {supportStatus.caseNumber && (
                    <span style={{ display: "block", marginTop: 6 }}>
                      Case Number: <strong>{supportStatus.caseNumber}</strong>
                    </span>
                  )}
                </div>
              )}

              {supportStatus.type === "error" && (
                <div
                  style={{
                    background: "#fee2e2",
                    color: "#b91c1c",
                    border: "1px solid #fecaca",
                    padding: "12px 16px",
                    borderRadius: 10,
                    marginBottom: 20,
                    fontWeight: 500,
                  }}
                >
                  {supportStatus.message}
                </div>
              )}

              <form onSubmit={handleSupportCaseSubmit}>
                <div style={{ marginBottom: 18 }}>
                  <label
                    style={{
                      display: "block",
                      marginBottom: 8,
                      fontWeight: 600,
                      color: "#0f172a",
                    }}
                  >
                    Subject *
                  </label>
                  <input
                    type="text"
                    name="subject"
                    value={supportCaseForm.subject}
                    onChange={handleSupportInputChange}
                    disabled={supportFieldsDisabled}
                    placeholder="e.g. Payment receipt from Vendor XYZ"
                    style={{
                      width: "100%",
                      padding: "12px 16px",
                      border: "2px solid #e2e8f0",
                      borderRadius: 10,
                      fontSize: 16,
                      opacity: supportFieldsDisabled ? 0.6 : 1,
                    }}
                    required
                  />
                </div>

                <div style={{ marginBottom: 18 }}>
                  <label
                    style={{
                      display: "block",
                      marginBottom: 8,
                      fontWeight: 600,
                      color: "#0f172a",
                    }}
                  >
                    Priority *
                  </label>
                  <select
                    name="priority"
                    value={supportCaseForm.priority}
                    onChange={handleSupportInputChange}
                    disabled={supportFieldsDisabled}
                    style={{
                      width: "100%",
                      padding: "12px 16px",
                      border: "2px solid #e2e8f0",
                      borderRadius: 10,
                      fontSize: 16,
                      background: "white",
                      opacity: supportFieldsDisabled ? 0.6 : 1,
                    }}
                  >
                    {priorityOptions.map((priority) => (
                      <option value={priority} key={priority}>
                        {priority.charAt(0) + priority.slice(1).toLowerCase()}
                      </option>
                    ))}
                  </select>
                </div>

                <div style={{ marginBottom: 20 }}>
                  <label
                    style={{
                      display: "block",
                      marginBottom: 8,
                      fontWeight: 600,
                      color: "#0f172a",
                    }}
                  >
                    Description *
                  </label>
                  <textarea
                    name="description"
                    value={supportCaseForm.description}
                    onChange={handleSupportInputChange}
                    rows={6}
                    disabled={supportFieldsDisabled}
                    placeholder="Share the full context, timelines, and people involved."
                    style={{
                      width: "100%",
                      padding: "12px 16px",
                      border: "2px solid #e2e8f0",
                      borderRadius: 10,
                      fontSize: 16,
                      resize: "vertical",
                      opacity: supportFieldsDisabled ? 0.6 : 1,
                    }}
                    required
                  />
                </div>

                <div style={{ marginBottom: 24 }}>
                  <label
                    style={{
                      display: "block",
                      marginBottom: 8,
                      fontWeight: 600,
                      color: "#0f172a",
                    }}
                  >
                    Attachment (optional)
                  </label>
                  <div
                    style={{
                      border: "2px dashed #cbd5f5",
                      borderRadius: 12,
                      padding: "14px 16px",
                      textAlign: "center",
                      background: "#f8fafc",
                    }}
                  >
                    <input
                      type="file"
                      accept=".png,.jpg,.jpeg,.pdf,.doc,.docx,.zip"
                      onChange={handleSupportAttachmentUpload}
                      disabled={
                        supportFieldsDisabled || supportAttachmentUploading
                      }
                      style={{ display: "none" }}
                      id="support-attachment-input"
                    />
                    <label
                      htmlFor="support-attachment-input"
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 10,
                        cursor:
                          supportFieldsDisabled || supportAttachmentUploading
                            ? "not-allowed"
                            : "pointer",
                        fontWeight: 600,
                        color: "#4f46e5",
                        opacity:
                          supportFieldsDisabled || supportAttachmentUploading
                            ? 0.6
                            : 1,
                      }}
                    >
                      {supportAttachmentUploading
                        ? "Uploading..."
                        : "Upload file"}
                    </label>
                    <p
                      style={{
                        fontSize: 13,
                        color: "#475569",
                        marginTop: 8,
                      }}
                    >
                      Attach up to 5MB. Accepted: PNG, JPG, PDF, DOC, ZIP
                    </p>
                    {supportAttachmentName && (
                      <div
                        style={{
                          marginTop: 10,
                          display: "flex",
                          justifyContent: "center",
                          gap: 10,
                          alignItems: "center",
                          flexWrap: "wrap",
                        }}
                      >
                        <span
                          style={{
                            fontSize: 14,
                            fontWeight: 600,
                            color: "#0f172a",
                          }}
                        >
                          {supportAttachmentName}
                        </span>
                        <button
                          type="button"
                          onClick={removeSupportAttachment}
                          style={{
                            border: "none",
                            background: "transparent",
                            color: "#b91c1c",
                            cursor: "pointer",
                            fontWeight: 600,
                          }}
                        >
                          Remove
                        </button>
                      </div>
                    )}
                    {supportAttachmentError && (
                      <p
                        style={{
                          color: "#b91c1c",
                          fontSize: 13,
                          marginTop: 8,
                        }}
                      >
                        {supportAttachmentError}
                      </p>
                    )}
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={supportSubmitDisabled}
                  style={{
                    width: "100%",
                    padding: "16px 24px",
                    borderRadius: 12,
                    border: "none",
                    fontSize: 18,
                    fontWeight: 700,
                    color: "white",
                    background: supportSubmitDisabled
                      ? "#cbd5f5"
                      : "linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)",
                    cursor: supportSubmitDisabled ? "not-allowed" : "pointer",
                    boxShadow: supportSubmitDisabled
                      ? "none"
                      : "0 20px 50px rgba(79,70,229,0.4)",
                    transition: "transform 0.2s ease, box-shadow 0.2s ease",
                  }}
                >
                  {supportSubmitting ? "Submitting..." : "Create Support Case"}
                </button>
                <p
                  style={{
                    marginTop: 14,
                    fontSize: 13,
                    color: "#6b7280",
                    textAlign: "center",
                  }}
                >
                  Our team usually responds within 4 business hours. Urgent cases
                  are escalated instantly.
                </p>
              </form>
            </div>
          </div>
        </div>
      </section>

      <section
        id="my-cases"
        style={{
          padding: "80px 0 120px",
          background: "linear-gradient(180deg, #eef2ff 0%, #ffffff 100%)",
        }}
      >
        <div
          style={{
            maxWidth: 1200,
            margin: "0 auto",
            padding: "0 20px",
          }}
        >
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 16,
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: 24,
              color: "white",
            }}
          >
            <div>
              <p
                style={{
                  textTransform: "uppercase",
                  letterSpacing: 4,
                  fontSize: 13,
                  color: "#7c3aed",
                  marginBottom: 6,
                }}
              >
                Track progress
              </p>
              <h3
                style={{
                  fontSize: 30,
                  fontWeight: 800,
                  margin: 0,
                  color: "#0f172a",
                }}
              >
                My Support Cases
              </h3>
              <p
                style={{
                  margin: "6px 0 0",
                  color: "#475569",
                  fontSize: 15,
                }}
              >
                Filter by status and review the latest updates.
              </p>
            </div>
            <select
              value={supportCaseFilter}
              onChange={handleSupportCaseFilterChange}
              disabled={!canUseSupport}
              style={{
                padding: "10px 18px",
                borderRadius: 12,
                border: "2px solid rgba(99,102,241,0.25)",
                background: "#ffffff",
                color: "#0f172a",
                fontWeight: 600,
                minWidth: 190,
              }}
            >
              {statusFilterOptions.map((statusOption) => (
                <option key={statusOption || "ALL"} value={statusOption}>
                  {statusOption
                    ? `Status: ${formatStatusLabel(statusOption)}`
                    : "All statuses"}
                </option>
              ))}
            </select>
          </div>

          {!canUseSupport && (
            <div
              style={{
                background: "#fef3c7",
                border: "1px solid #fde68a",
                borderRadius: 20,
                padding: 30,
                textAlign: "center",
                color: "#92400e",
                fontWeight: 500,
              }}
            >
              Please log in as a client to view your support history.
            </div>
          )}

          {canUseSupport && (
            <div
              style={{
                border: "1px solid rgba(99,102,241,0.15)",
                borderRadius: 24,
                padding: 28,
                background: "#ffffff",
                boxShadow: "0 25px 50px rgba(15,23,42,0.08)",
              }}
            >
              {supportCasesLoading && (
                <p
                  style={{
                    textAlign: "center",
                    color: "#475569",
                    fontWeight: 600,
                    margin: 0,
                  }}
                >
                  Loading cases...
                </p>
              )}

              {!supportCasesLoading && supportCasesError && (
                <div
                  style={{
                    background: "#fee2e2",
                    color: "#b91c1c",
                    border: "1px solid #fecaca",
                    padding: "12px 16px",
                    borderRadius: 12,
                    fontWeight: 500,
                    textAlign: "center",
                  }}
                >
                  {supportCasesError}
                </div>
              )}

              {!supportCasesLoading &&
                !supportCasesError &&
                supportCases.length === 0 && (
                  <div
                    style={{
                      textAlign: "center",
                      padding: "30px 10px",
                      color: "#475569",
                      fontWeight: 500,
                    }}
                  >
                    No support cases yet. Submit your first case above!
                  </div>
                )}

              {!supportCasesLoading &&
                !supportCasesError &&
                supportCases.length > 0 && (
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
                      gap: 20,
                    }}
                  >
                    {supportCases.map((supportCase) => {
                      const priorityColors = getPriorityBadgeColors(
                        supportCase.priority
                      );
                      const statusColors = getStatusBadgeColors(
                        supportCase.status
                      );
                      return (
                        <div
                          key={supportCase.id}
                          style={{
                            background: "#f8fafc",
                            borderRadius: 20,
                            padding: 20,
                            border: "1px solid rgba(148,163,184,0.35)",
                            boxShadow: "0 15px 35px rgba(15,23,42,0.08)",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              gap: 12,
                              alignItems: "flex-start",
                              marginBottom: 16,
                            }}
                          >
                            <div>
                              <p
                                style={{
                                  margin: 0,
                                  fontSize: 14,
                                  color: "#475569",
                                }}
                              >
                                {supportCase.caseNumber}
                              </p>
                              <h5
                                style={{
                                  margin: "6px 0 0",
                                  fontSize: 20,
                                  color: "#0f172a",
                                  lineHeight: 1.3,
                                }}
                              >
                                {supportCase.subject}
                              </h5>
                            </div>
                            <div style={{ textAlign: "right" }}>
                              <span
                                style={{
                                  display: "inline-block",
                                  padding: "4px 10px",
                                  borderRadius: 999,
                                  fontSize: 12,
                                  fontWeight: 700,
                                  marginBottom: 6,
                                  background: priorityColors.background,
                                  color: priorityColors.color,
                                }}
                              >
                                {formatStatusLabel(supportCase.priority)}
                              </span>
                              <span
                                style={{
                                  display: "inline-block",
                                  padding: "4px 10px",
                                  borderRadius: 999,
                                  fontSize: 12,
                                  fontWeight: 700,
                                  background: statusColors.background,
                                  color: statusColors.color,
                                }}
                              >
                                {formatStatusLabel(supportCase.status)}
                              </span>
                            </div>
                          </div>

                          <div
                            style={{
                              display: "grid",
                              gap: 12,
                              fontSize: 14,
                                color: "#475569",
                            }}
                          >
                            <div>
                                <strong style={{ color: "#0f172a" }}>
                                Latest Update:
                              </strong>{" "}
                              {formatCaseDate(
                                supportCase.latestActivityAt ||
                                  supportCase.createdAt
                              )}
                            </div>
                            <div>
                                <strong style={{ color: "#0f172a" }}>
                                Created:
                              </strong>{" "}
                              {formatCaseDate(supportCase.createdAt)}
                            </div>
                            <div>
                                <strong style={{ color: "#0f172a" }}>
                                Last Responder:
                              </strong>{" "}
                              {formatStatusLabel(
                                supportCase.lastResponderType || "CLIENT"
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default SupportCenter;
